import {getSubscriptionModules, hasActiveSubscriptionModule} from '../util/requestSender';
import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {ModelResponse, ResponseData} from '@dvrd/dvr-controls';
import {SubscriptionModuleName, SubscriptionModuleStatus} from '../util/enums';
import IDate from '@dvrd/idate';

interface Props extends BaseProps {
    name?: SubscriptionModuleName;
    charge_per?: string;
    end_per?: string | null;
    quantity?: number;
    status?: SubscriptionModuleStatus;
    flex?: Record<string, any> | null;
}

export interface SubscriptionModuleFields extends BaseFields {
    name: SubscriptionModuleName;
    charge_per: string;
    end_per: string | null;
    quantity: number;
    status: SubscriptionModuleStatus;
    flex: Record<string, any> | null;
}

const fields: SubscriptionModuleFields = {
    id: -1n,
    created_at: '',
    updated_at: '',
    name: SubscriptionModuleName.NOTIFICATIONS,
    charge_per: '',
    end_per: '',
    quantity: 0,
    status: SubscriptionModuleStatus.TRIAL,
    flex: null
};

const fieldsDefinition: { [key in keyof SubscriptionModuleFields]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.BIGINT),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    charge_per: createFieldDef(ModelFieldType.STRING),
    end_per: createFieldDef(ModelFieldType.STRING, null, true),
    flex: createFieldDef(ModelFieldType.OBJECT, null, true),
    name: createFieldDef(ModelFieldType.STRING, SubscriptionModuleName.NOTIFICATIONS),
    quantity: createFieldDef(ModelFieldType.NUMBER),
    status: createFieldDef(ModelFieldType.STRING, SubscriptionModuleStatus.TRIAL)
};

const ignoreProps: (keyof SubscriptionModuleFields)[] = [];

export default class SubscriptionModule extends BaseModel<Props, SubscriptionModuleFields> {
    static getAll = (accountID: string, callback: ModelResponse<SubscriptionModule[]>) => {
        return getSubscriptionModules({
            account_id: accountID, callback: (data: ResponseData) => {
                const modules: Array<SubscriptionModule> = data.success ? data.modules.map(
                    (module: any) => new SubscriptionModule(module)) : [];
                callback(modules, data.success, data);
            }
        });
    };

    static hasActiveModule = (accountID: string, moduleName: SubscriptionModuleName,
                              callback: ModelResponse<boolean>) => {
        return hasActiveSubscriptionModule({
            account_id: accountID, name: moduleName, callback: (data: ResponseData) => {
                const hasModule: boolean = data.has_active_module ?? false;
                callback(hasModule, data.success, data);
            }
        });
    }

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get name(): SubscriptionModuleName {
        return this.fields.name;
    }

    set name(name: SubscriptionModuleName) {
        this.fields.name = name;
    }

    get chargePer(): string {
        return this.fields.charge_per;
    }

    set chargePer(chargePer: string) {
        this.fields.charge_per = chargePer;
    }

    get chargePerDate(): IDate {
        return new IDate(this.fields.charge_per);
    }

    get endPer(): string | null {
        return this.fields.end_per;
    }

    set endPer(end_per: string | null) {
        this.fields.end_per = end_per;
    }

    get endPerDate(): IDate | null {
        if (!this.fields.end_per) return null;
        return new IDate(this.fields.end_per);
    }

    get quantity(): number {
        return this.fields.quantity;
    }

    set quantity(quantity: number) {
        this.fields.quantity = quantity;
    }

    get status(): SubscriptionModuleStatus {
        return this.fields.status;
    }

    set status(status: SubscriptionModuleStatus) {
        this.fields.status = status;
    }

    get flex(): Record<string, any> | null {
        return this.fields.flex;
    }

    set flex(flex: Record<string, any> | null) {
        this.fields.flex = flex;
    }

    isActive = (excludeStatuses: Array<SubscriptionModuleStatus> = []): boolean => {
        if (this.status === SubscriptionModuleStatus.TRIAL) {
            if (excludeStatuses.includes(SubscriptionModuleStatus.TRIAL)) return false;
            return this.chargePerDate.isAfter(IDate.now());
        }
        if (this.status === SubscriptionModuleStatus.CANCELED) return false;
        return !excludeStatuses.includes(this.status);
    }

    isTrialing = (includePaid: boolean = true): boolean => {
        if ([SubscriptionModuleStatus.FREE, SubscriptionModuleStatus.CANCELED].includes(this.status)) return false;
        if (!includePaid && this.status === SubscriptionModuleStatus.PAID) return false;
        return this.chargePerDate.isAfter(IDate.now());
    }

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
        }
    };
}